import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { VolumeOff, VolumeUp } from "@mui/icons-material";
import { Button, Tooltip } from "@mui/material";

import { useApi } from "@framework/provider-api-jwt";
import { RangeType, calculateStartDate, useAsu } from "@framework/provider-asu";
import { IUserDto, useUser } from "@framework/provider-user";

import { getSelectedItemType, SELECTED_ITEM_TYPE } from "../../../../utils";
import { SOUND_ALARM_REFRESH_EVENT, SOUND_ALARM_SIGNAL_INTERVAL_MS } from "./constants";
import { UserVersion } from "@framework/types";

const SOUND_ALARM_SUPPORTED_VERSIONS = new Set([UserVersion.PROFESSIONAL.toString()]);

interface IHasUnacknowledgedEventsResponse {
  hasUnacknowledgedEvents: boolean;
}

interface IWebkitAudioWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

type TUnacknowledgedEventsRequestData = {
  endTimestamp: string;
  startTimestamp: string;
} & ({ idTopLevel: number } | { idTrain: number });

interface IUnacknowledgedEventsRequestConfig {
  data: TUnacknowledgedEventsRequestData;
  url: "/events/station/unacknowledged" | "/events/train/unacknowledged";
}

const toIsoString = (value: string | Date | null | undefined) => {
  if (!value) {
    return null;
  }

  return typeof value === "string" ? value : value.toISOString();
};

const isAbortError = (error: unknown) => (error as Error)?.name === "AbortError";

export const SoundAlarmButton: FC = () => {
  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    userSelectedRangeType,
    userSelectedRecorderModePeriod,
    recorderModeInterval,
    isRecorderModeActive,
  } = useAsu();
  const api = useApi();
  const user = useUser<IUserDto>();
  const { formatMessage } = useIntl();

  const isAuthenticated = user.isAuthenticated();
  const normalizedVersion = String(user.profile?.version || "").toLowerCase();
  const hasSupportedVersion = isAuthenticated && SOUND_ALARM_SUPPORTED_VERSIONS.has(normalizedVersion);

  const [isEnabled, setIsEnabled] = useState(false);
  const [hasUnacknowledgedEvents, setHasUnacknowledgedEvents] = useState(false);

  const selectedItemType = useMemo(() => getSelectedItemType(selectedItem), [selectedItem]);
  const selectedStationId = selectedItem && "Id" in selectedItem ? selectedItem.Id : null;
  const selectedTrainId = selectedItem && "TrainId" in selectedItem ? selectedItem.TrainId : null;

  const effectiveEndTimestamp = useMemo(() => toIsoString(endTimestamp), [endTimestamp]);
  const effectiveStartTimestamp = useMemo(() => {
    if (!effectiveEndTimestamp) {
      return null;
    }

    if (userSelectedRangeType === RangeType.RECORDER_MODE) {
      if (!isRecorderModeActive || recorderModeInterval <= 0 || !userSelectedRecorderModePeriod) {
        return null;
      }

      return calculateStartDate(
        new Date(effectiveEndTimestamp),
        userSelectedRecorderModePeriod,
        recorderModeInterval,
      ).toISOString();
    }

    return toIsoString(startTimestamp);
  }, [
    effectiveEndTimestamp,
    isRecorderModeActive,
    recorderModeInterval,
    startTimestamp,
    userSelectedRangeType,
    userSelectedRecorderModePeriod,
  ]);

  const requestConfig = useMemo<IUnacknowledgedEventsRequestConfig | null>(() => {
    if (!effectiveStartTimestamp || !effectiveEndTimestamp) {
      return null;
    }

    if (selectedItemType === SELECTED_ITEM_TYPE.station && selectedStationId) {
      return {
        url: "/events/station/unacknowledged",
        data: {
          idTopLevel: selectedStationId,
          startTimestamp: effectiveStartTimestamp,
          endTimestamp: effectiveEndTimestamp,
        },
      };
    }

    if (selectedItemType === SELECTED_ITEM_TYPE.train && selectedTrainId) {
      return {
        url: "/events/train/unacknowledged",
        data: {
          idTrain: selectedTrainId,
          startTimestamp: effectiveStartTimestamp,
          endTimestamp: effectiveEndTimestamp,
        },
      };
    }

    return null;
  }, [effectiveEndTimestamp, effectiveStartTimestamp, selectedItemType, selectedStationId, selectedTrainId]);

  const requestIdRef = useRef(0);
  const requestAbortControllerRef = useRef<AbortController | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const invalidatePendingRequest = useCallback(() => {
    requestIdRef.current += 1;

    if (requestAbortControllerRef.current) {
      requestAbortControllerRef.current.abort();
      requestAbortControllerRef.current = null;
    }
  }, []);

  const ensureAudioContext = useCallback(async () => {
    const AudioContextConstructor = window.AudioContext || (window as IWebkitAudioWindow).webkitAudioContext || null;

    if (!AudioContextConstructor) {
      return null;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextConstructor();
    }

    if (audioContextRef.current.state === "suspended") {
      try {
        await audioContextRef.current.resume();
      } catch {
        return null;
      }
    }

    return audioContextRef.current;
  }, []);

  const playWarningSignal = useCallback(async () => {
    const audioContext = await ensureAudioContext();

    if (!audioContext) {
      return;
    }

    const scheduleTone = (offset: number, frequency: number) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const startAt = audioContext.currentTime + offset;

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, startAt);

      gainNode.gain.setValueAtTime(0.0001, startAt);
      gainNode.gain.exponentialRampToValueAtTime(0.18, startAt + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.18);

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.start(startAt);
      oscillator.stop(startAt + 0.22);
    };

    scheduleTone(0, 880);
    scheduleTone(0.28, 660);
  }, [ensureAudioContext]);

  const refreshUnacknowledgedEvents = useCallback(async () => {
    if (!requestConfig) {
      setHasUnacknowledgedEvents(false);
      return;
    }

    invalidatePendingRequest();

    const requestId = requestIdRef.current;
    const controller = new AbortController();
    requestAbortControllerRef.current = controller;

    try {
      const response = (await api.fetchJson({
        url: requestConfig.url,
        data: requestConfig.data,
        signal: controller.signal,
      })) as IHasUnacknowledgedEventsResponse | null;

      if (requestId !== requestIdRef.current) {
        return;
      }

      setHasUnacknowledgedEvents(Boolean(response?.hasUnacknowledgedEvents));
    } catch (error) {
      if (!isAbortError(error)) {
        setHasUnacknowledgedEvents(false);
        console.error(error);
      }
    } finally {
      if (requestAbortControllerRef.current === controller) {
        requestAbortControllerRef.current = null;
      }
    }
  }, [api, invalidatePendingRequest, requestConfig]);

  useEffect(() => {
    if (!isEnabled || !hasSupportedVersion) {
      invalidatePendingRequest();
      setHasUnacknowledgedEvents(false);
      return;
    }

    if (!requestConfig) {
      invalidatePendingRequest();
      setHasUnacknowledgedEvents(false);
      return;
    }

    void refreshUnacknowledgedEvents();
  }, [hasSupportedVersion, invalidatePendingRequest, isEnabled, refreshUnacknowledgedEvents, requestConfig]);

  useEffect(() => {
    if (!isEnabled || !hasSupportedVersion) {
      return;
    }

    const handleRefresh = () => {
      void refreshUnacknowledgedEvents();
    };

    window.addEventListener(SOUND_ALARM_REFRESH_EVENT, handleRefresh);

    return () => {
      window.removeEventListener(SOUND_ALARM_REFRESH_EVENT, handleRefresh);
    };
  }, [hasSupportedVersion, isEnabled, refreshUnacknowledgedEvents]);

  useEffect(() => {
    if (!isEnabled || !hasUnacknowledgedEvents) {
      return;
    }

    void playWarningSignal();

    const intervalId = window.setInterval(() => {
      void playWarningSignal();
    }, SOUND_ALARM_SIGNAL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasUnacknowledgedEvents, isEnabled, playWarningSignal]);

  useEffect(() => {
    return () => {
      invalidatePendingRequest();

      if (audioContextRef.current) {
        void audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, [invalidatePendingRequest]);

  const handleToggle = () => {
    const nextIsEnabled = !isEnabled;

    setIsEnabled(nextIsEnabled);

    if (nextIsEnabled) {
      void ensureAudioContext();
      return;
    }

    invalidatePendingRequest();
    setHasUnacknowledgedEvents(false);
  };

  if (!hasSupportedVersion) {
    return null;
  }

  const isAlerting = isEnabled && hasUnacknowledgedEvents;
  const tooltipTitle = formatMessage({
    id: `components.header.soundAlarm.${isEnabled ? "disable" : "enable"}`,
  });

  const volumeIcon = isEnabled ? <VolumeUp /> : <VolumeOff />;

  return (
    <Tooltip title={tooltipTitle} enterDelay={300}>
      <Button
        onClick={handleToggle}
        aria-pressed={isEnabled}
        aria-label={formatMessage({ id: "components.header.soundAlarm.label" })}
        variant="outlined"
        size="small"
        sx={{
          minWidth: 40,
          height: 40,
          px: 0,
          borderRadius: "8px",
          textTransform: "none",
          whiteSpace: "nowrap",
          fontWeight: 700,
          color: isAlerting ? "#FFFFFF" : "#363846",
          backgroundColor: isAlerting ? "#D95D39" : isEnabled ? "#F4F6FB" : "#FFFFFF",
          borderColor: isAlerting ? "#D95D39" : isEnabled ? "#A9AEC3" : "#EBEBF7",
          boxShadow: isAlerting ? "0 0 0 2px rgba(217, 93, 57, 0.16)" : "none",
          "&:hover": {
            backgroundColor: isAlerting ? "#C55335" : isEnabled ? "#EBEEF7" : "#F7F8FC",
            borderColor: isAlerting ? "#C55335" : isEnabled ? "#9097B0" : "#D9DCEA",
          },
          "& .MuiButton-startIcon": {
            mr: 0,
          },
        }}
      >
        {volumeIcon}
      </Button>
    </Tooltip>
  );
};
